import { useI18n } from '../../../i18n';
// ============================================
// OrdersFilterBar - Filter controls for orders
// ============================================

import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

interface OrdersFilterBarProps {
  // Filters
  selectedStore: string | null;
  selectedStatus: string | null;
  selectedSupplier: string | null;
  dateRange: { start: string; end: string } | null;
  // Handlers
  onStoreChange: (value: string | null) => void;
  onStatusChange: (value: string | null) => void;
  onSupplierChange: (value: string | null) => void;
  onDateRangeChange: (range: { start: string; end: string } | null) => void;
  onClearFilters: () => void;
  // Options
  stores: Array<{ id: string; name: string }>;
  suppliers: Array<{ id: string; name: string }>;
  className?: string;
}

// ============================================


// ============================================
// Icons
// ============================================

function ChevronDownIcon() {
  return (
    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
  );
}

// ============================================
// Select Component
// ============================================

function Select({
  value,
  onChange,
  options,
  placeholder = 'Select...',
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}) {
  return (
    <div className="relative">
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        className={cn(
          'appearance-none w-full h-9 pl-3 pr-8 rounded-lg',
          'bg-[#E7E7F3] border-0 text-sm text-[#191B23]',
          'focus:outline-none focus:ring-2 focus:ring-primary-500'
        )}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
        <ChevronDownIcon />
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function OrdersFilterBar({
  selectedStore,
  selectedStatus,
  selectedSupplier,
  dateRange,
  onStoreChange,
  onStatusChange,
  onSupplierChange,
  onDateRangeChange,
  onClearFilters,
  stores,
  suppliers,
  className,
}: OrdersFilterBarProps) {
  const { t } = useI18n();
  // Khớp OrderStatus của BE. PENDING/PROCESSING cũ không tồn tại trong DB
  // nên filter theo chúng luôn trả rỗng.
  const statusOptions = [
    { value: 'DRAFT', label: t('draft') },
    { value: 'PENDING_PAYMENT', label: t('pendingPayment') },
    { value: 'CONFIRMED', label: t('confirmed') },
    { value: 'PREPARING', label: t('preparing') },
    { value: 'READY', label: t('ready') },
    { value: 'PAID', label: t('paid') },
    { value: 'COMPLETED', label: t('completed') },
    { value: 'CANCELLED', label: t('cancelled') },
    { value: 'VOIDED', label: t('voided') },
    { value: 'REFUNDED', label: t('refunded') },
  ];
  const hasFilters = selectedStore || selectedStatus || selectedSupplier || dateRange;

  return (
    <div
      className={cn(
        'bg-white rounded-xl shadow-sm p-4 flex items-center gap-6 flex-wrap',
        className
      )}
    >
      {/* Filter By Section */}
      <div className="flex w-full flex-wrap items-center gap-3 animate-fade-in sm:w-auto">
        <span className="text-sm font-bold text-[#434655] flex items-center gap-1.5">
          <FilterIcon />
          {t('filterBy')}
        </span>

        {/* Store + Status + Supplier: 2 cột trên mobile, xếp ngang trên desktop */}
        <div className="grid w-full grid-cols-2 gap-3 sm:contents">
          {/* Store/Destination */}
          <Select
            value={selectedStore}
            onChange={onStoreChange}
            options={stores.map((s) => ({ value: s.id, label: s.name }))}
            placeholder={t('allDestinations')}
          />

          {/* Status */}
          <Select
            value={selectedStatus}
            onChange={onStatusChange}
            options={statusOptions}
            placeholder={t('allStatuses')}
          />

          {/* Supplier */}
          {suppliers.length > 0 && (
            <Select
              value={selectedSupplier}
              onChange={onSupplierChange}
              options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
              placeholder={t('allSuppliers')}
            />
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="hidden h-8 w-px bg-[rgba(195,198,215,0.3)] sm:block" />

      {/* Date Range Section */}
      <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
        <span className="text-sm font-bold text-[#434655]">{t('dateLabel')}</span>
        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 sm:flex sm:w-auto">
          <input
            type="date"
            value={dateRange?.start || ''}
            onChange={(e) => {
              const start = e.target.value;
              const end = dateRange?.end || new Date().toISOString().split('T')[0];
              if (start) {
                onDateRangeChange({ start, end });
              } else {
                onDateRangeChange(null);
              }
            }}
            className="h-9 w-full min-w-0 flex-1 px-3 rounded-lg bg-[#E7E7F3] border-0 text-sm text-[#191B23] focus:outline-none focus:ring-2 focus:ring-primary-500 sm:w-auto sm:flex-none"
          />
          <span className="text-xs text-slate-400">{t('to') || 'to'}</span>
          <input
            type="date"
            value={dateRange?.end || ''}
            onChange={(e) => {
              const end = e.target.value;
              const start = dateRange?.start || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
              if (end) {
                onDateRangeChange({ start, end });
              } else {
                onDateRangeChange(null);
              }
            }}
            className="h-9 w-full min-w-0 flex-1 px-3 rounded-lg bg-[#E7E7F3] border-0 text-sm text-[#191B23] focus:outline-none focus:ring-2 focus:ring-primary-500 sm:w-auto sm:flex-none"
          />
        </div>
      </div>

      {/* Clear Filters */}
      {hasFilters && (
        <button
          onClick={onClearFilters}
          className="ml-auto text-sm font-bold text-primary-600 hover:text-primary-700 transition-colors"
        >
          {t('clearFilters')}
        </button>
      )}
    </div>
  );
}
